import { ReactNode, useEffect, useState } from 'react'
import useUserStore from '@/user/userStore'
import { useFetchUser } from '@/user/userQueries'
import { triggerSetupUser } from '@/user/userApi'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { analytics } from '@/common/analytics/analytics'
import { getStoredUTMParameters } from '@/common/analytics/utm'
import { useIsStandalone } from '@/common/pwa/webauthn'
import { NotFoundError } from '@/common/api/error'
import { UserContext } from './useUser'

const UserProvider = ({ children }: { children: ReactNode }) => {
    const [ready, setReady] = useState(false)
    const installedPwa = useIsStandalone()

    const { setUser, user: storedUser } = useUserStore()

    const { data: user, error: userError, isPending: isUserPending, status: userStatus } = useFetchUser(true)

    useEffect(() => {
        if (user) return
        if (isUserPending) return

        const shouldRecoverMissingUser = userStatus === 'error' && userError instanceof NotFoundError
        if (!shouldRecoverMissingUser) return

        const runSetup = async () => {
            const newUser = await triggerSetupUser()
            setUser(newUser)
            setReady(true)
        }

        runSetup()
    }, [user, isUserPending, userStatus, userError, setUser])

    useEffect(() => {
        if (!user) return
        setUser(user)
    }, [user, setUser])

    useEffect(() => {
        if (!user) return

        // Identify user early (before account resolution). AccountProvider will re-identify
        // with the actual accountId once an account is selected.
        analytics.identify(user.userId, {
            accountId: null,
            email: user.emailId,
            firstName: user.firstName ?? undefined,
            lastName: user.lastName ?? undefined,
            utm: getStoredUTMParameters(),
            ...(installedPwa ? { pwa: true } : {}),
        })
    }, [user, installedPwa])

    useEffect(() => {
        if (user) {
            setReady(true)
        }
    }, [user])

    if (!ready) {
        return <FullScreenLoader dark />
    }

    return <UserContext.Provider value={{ user: user ?? storedUser ?? null }}>{children}</UserContext.Provider>
}

export default UserProvider
